/**
 * guestsV2Store — estado de sesión del pre-check-in.
 *
 * Mismo patrón que `settingsV2Store` / `todayV2Store` / `reservationsV2Store`:
 * un módulo, `useSyncExternalStore`, cero persistencia. Al recargar vuelve la
 * semilla.
 *
 * Guarda dos cosas, y nada más:
 *   - lo que el huésped envía desde su enlace (`submitPreCheckIn`);
 *   - lo que recepción comprueba o presenta (`verifyDocuments`, `fileImmigration`).
 *
 * Que estén en el mismo módulo no los mezcla: son campos distintos y ninguna
 * función escribe en los dos. El pre-check-in es del huésped; el reporte de
 * inmigración es de la propiedad.
 *
 * No llama a ningún endpoint. El flujo del huésped es de demostración y no
 * muta nada de producción.
 */
import { useSyncExternalStore } from "react";
import { PRE_CHECKIN_SUBMISSIONS, IMMIGRATION_CASES, GUESTS } from "@/data/guestsV2Demo";

function seed() {
  return {
    // reservation_id -> { submitted_at, arrival, preferences }
    submissions: Object.fromEntries(
      Object.entries(PRE_CHECKIN_SUBMISSIONS).map(([k, v]) => [k, JSON.parse(JSON.stringify(v))]),
    ),
    // guest_id -> parches por persona (nombre, nacionalidad, documento…)
    guestPatches: {},
    // reservation_id -> { state, filed_at, actor }
    immigration: Object.fromEntries(
      Object.entries(IMMIGRATION_CASES).map(([k, v]) => [k, { ...v }]),
    ),
    // guest_id de las personas dadas de alta en esta sesión
    sessionGuests: [],
    tick: 0,
  };
}

let state = seed();
const listeners = new Set();

function emit(next) {
  state = next;
  for (const fn of listeners) fn();
}

function commit(patch) {
  emit({ ...state, ...patch, tick: state.tick + 1 });
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getGuestsV2() {
  return state;
}

export function useGuestsV2() {
  return useSyncExternalStore(subscribe, getGuestsV2);
}

/** Solo para tests. */
export function resetGuestsV2() {
  sessionGuestCounter = 0;
  emit(seed());
}

// ---------------------------------------------------------------------------
// El huésped
// ---------------------------------------------------------------------------

let sessionGuestCounter = 0;

/**
 * Envío del pre-check-in desde el enlace del huésped.
 *
 * Escribe SOLO lo que el huésped puede responder: sus datos, su llegada y sus
 * preferencias. Deliberadamente no toca `immigration`: el formulario del
 * huésped no puede mover el reporte de la propiedad ni aunque lo rellene
 * entero.
 */
export function submitPreCheckIn(reservationId, { guests = [], arrival, preferences, at }) {
  if (!reservationId) return;

  const patches = { ...state.guestPatches };
  const created = [...state.sessionGuests];

  for (const g of guests) {
    if (g.guest_id) {
      patches[g.guest_id] = { ...(patches[g.guest_id] || {}), ...g };
      continue;
    }
    // Acompañante nuevo añadido desde el formulario.
    if (!String(g.full_name || "").trim()) continue;
    sessionGuestCounter += 1;
    const id = `G-SESSION-${String(sessionGuestCounter).padStart(3, "0")}`;
    patches[id] = {
      guest_id: id,
      reservation_id: reservationId,
      primary_guest: false,
      document: { status: "not_provided", type: null },
      email: null,
      phone: null,
      ...g,
      origin: "created_in_session",
    };
    created.push(id);
  }

  commit({
    guestPatches: patches,
    sessionGuests: created,
    submissions: {
      ...state.submissions,
      [reservationId]: {
        ...(state.submissions[reservationId] || {}),
        submitted_at: at || null,
        ...(arrival ? { arrival } : {}),
        ...(preferences ? { preferences } : {}),
      },
    },
  });
}

// ---------------------------------------------------------------------------
// La propiedad
// ---------------------------------------------------------------------------

/** Recepción comprueba el documento de una persona contra quien tiene delante. */
export function verifyDocument(guestId) {
  if (!guestId) return;
  const prev = state.guestPatches[guestId] || {};
  const base = GUESTS.find((g) => g.guest_id === guestId);
  const current = prev.document || base?.document || { status: "not_provided", type: null };
  commit({
    guestPatches: {
      ...state.guestPatches,
      [guestId]: { ...prev, document: { ...current, status: "verified" } },
    },
  });
}

/**
 * La propiedad presenta el reporte de extranjeros. Es el ÚNICO camino por el
 * que una reserva pasa a `filed`: no hay inferencia posible desde el
 * pre-check-in ni desde la estancia.
 */
export function fileImmigration(reservationId, { actor, at } = {}) {
  if (!reservationId) return;
  commit({
    immigration: {
      ...state.immigration,
      [reservationId]: { state: "filed", filed_at: at || null, actor: actor || "Equipo" },
    },
  });
}
